import React, { useState, useEffect } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { GradeBadge } from '@/components/GradeBadge';
import type { AdminDayConversationResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './AdminDayConversations.css';
import '../styles/common.css';

const AdminDayConversations: React.FC = () => {
  const [conversations, setConversations] = useState<AdminDayConversationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [moduleName, setModuleName] = useState<string>('');
  const [className, setClassName] = useState<string>('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const moduleId = searchParams.get('moduleId');
  const date = searchParams.get('date');
  const classId = searchParams.get('classId');
  const pageTitle = [moduleName, className].filter(Boolean).join(' - ');

  useEffect(() => {
    fetchUserInfo();
    if (moduleId && date && classId) {
      fetchConversations();
      fetchNames();
    } else {
      setError('Module ID, date, and class ID are required');
      setLoading(false);
    }
  }, [moduleId, date, classId]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchNames = async () => {
    if (!moduleId || !classId) return;
    const [mName, cName] = await Promise.all([
      adminService.getModuleNameById(moduleId),
      adminService.getClassNameById(classId),
    ]);
    setModuleName(mName);
    setClassName(cName);
  };

  const fetchConversations = async () => {
    if (!moduleId || !date || !classId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getConversationsByModuleAndDate(moduleId, date, classId);
      setConversations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };


  const handleViewConversation = (conv: AdminDayConversationResponse) => {
    sessionStorage.setItem('adminModuleId', moduleId || '');
    sessionStorage.setItem('adminUserId', conv.userId);
    sessionStorage.setItem('adminUserFirstName', conv.userFirstName);
    sessionStorage.setItem('adminUserLastName', conv.userLastName);
    sessionStorage.setItem('adminBackTo', 'days');
    sessionStorage.setItem('adminBackDate', date || '');
    sessionStorage.setItem('adminBackClassId', classId || '');
    navigate(`/admin/conversation?conversationId=${conv.id}`);
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'long' });
    const year = d.getFullYear();
    const weekday = d.toLocaleString('en-US', { weekday: 'long' });
    return `${weekday}, ${month} ${day}, ${year}`;
  };

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  return (
    <div className="admin-day-conversations-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle={date ? `${pageTitle ? `${pageTitle} - ` : ''}Conversations from ${formatDate(date)}` : 'Conversations'} 
      />
      
      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              { label: 'Modules', path: '/admin/modules' },
              { label: pageTitle ? `${pageTitle} - Days` : 'Days', path: `/admin/days?${new URLSearchParams({ moduleId: moduleId || '', classId: classId || '' })}` },
              { label: date ? `Conversations from ${formatDate(date)}` : 'Conversations' },
            ]} />
            <h2 className="navigation-bar-title">{date ? `${pageTitle ? `${pageTitle} - ` : ''}Conversations from ${formatDate(date)}` : 'Conversations'}</h2>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchConversations} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">💬</div>
            <h3>No conversations found</h3>
            <p>There were no conversations on this day.</p>
          </div>
        ) : (
          <InlineList compact>
            {conversations.map(conv => (
              <InlineListRow
                key={conv.id}
                className={`${conv.isDiscussed ? 'discussed' : ''} ${conv.isAdjusted ? 'adjusted' : ''} ${conv.isAborted ? 'aborted' : ''}`}
                actions={
                  <div className="conversation-status">
                    {conv.isAborted && <span className="status-badge aborted-badge">Aborted</span>}
                    <GradeBadge grade={conv.grade} />
                  </div>
                }
                onClick={() => handleViewConversation(conv)}
              >
                <span className="admin-subject-number">{conv.subjectNumber}</span>
                <div className="admin-day-conv-info">
                  <h3 className="admin-day-conv-title">{conv.subjectTitle}</h3>
                  <span className="admin-day-conv-meta">{conv.userFirstName} {conv.userLastName} ({conv.username}) &middot; {formatTime(conv.createdAt)}</span>
                </div>
              </InlineListRow>
            ))}
          </InlineList>
        )}
      </main>
    </div>
  );
};

export default AdminDayConversations;
